import { List, Map } from 'immutable'
import { AsyncState, HttpResult, loadingAsyncState } from 'widgets-for-react'
import { CMSLoaderState } from '../cmsPage.state'
import { getLangFromDocument, getLangFromUrl, removeLangFromUrl } from '../utils/history'
import { htmlDecode } from '../utils/html'
import { linkWrapper } from '../utils/link'
import { parseWordpressImageUrls } from '../utils/modernImage'
import { MenuLocationResponse } from './api/types'
import { Gutenberg } from './blockDefinitions'

export interface Headers {
  'X-WP-Nonce'?: string
}

export namespace Store {
  let store: Map<string, any> = Map<string, any>()

  export function read<T>(key: string): T {
    return store.get(key)
  }

  export function write<T>(key: string, data: T) {
    store = store.set(key, data)
    return data
  }
}

export type PaginationResponse<R> = {
  posts: R,
  pagination: {
    total_pages: number,
    total_posts: number
  }
}

export namespace WordPress {

  export const findMenuByLocation = async (location: string): Promise<MenuLocationResponse> => {


    let res = await fetch(`/api/wp/get${location}?lang=${getLangFromDocument()}`)
    let response = await res.json()

    if (response) {

      return response.items
    }
    return []
  }


  export const getPage: (lang: string, slug: string) => Promise<HttpResult<CMSLoaderState>> = async (lang, slug) => {
    try {

      if (slug == "" || slug == undefined) {
        slug = "/"
      }

      let resPre = await fetch(`/api/wp/getPage?slug=${removeLangFromUrl(slug)}&lang=${getLangFromDocument()}`)

      if (!resPre.ok) {
        resPre = await fetch('/api/wp/getPage?slug=not-found')
      }
      
      let res = await resPre.json()

      const blocks: Array<Gutenberg.Block> = res.blocks_custom
      const headerBlock = <Gutenberg.Vidda.Hero>blocks.find((b) => b.blockName == 'vidda/header')

      const linkPrimary =
        headerBlock?.attrs?.show_primary &&
        headerBlock?.attrs?.primary_button_text &&
        headerBlock?.attrs?.primary_button_url
          ? linkWrapper({
            url: headerBlock.attrs.primary_button_url,
            title: htmlDecode(headerBlock.attrs.primary_button_text)
            }) 
          : undefined

      const linkSecondary =
        headerBlock?.attrs?.show_secondary &&
        headerBlock?.attrs?.secondary_button_text &&
        headerBlock?.attrs?.secondary_button_url
          ? linkWrapper({
              url: headerBlock?.attrs?.secondary_button_url,
              title: htmlDecode(headerBlock?.attrs?.secondary_button_text)
            })
          : undefined

      const breadcrumbs = res?.breadcrumb && !('errors' in res.breadcrumb)
        ? List(
            res.breadcrumb.itemListElement.map((i: any) => ({
              url: i.item['@id'], //add url here
              title: i.item.name
            }))
          )
        : List()

      const pageKind = res?.is_front
        ? 'home'
        : res?.type == 'preview'
          ? res?.preview_of?.type == 'page'
            ? 'site'
            : 'resource'
          : res?.type == 'page'
            ? 'site'
            : 'resource'

      const headerKind: 'normal' | 'fullscreen' | 'home' = res?.is_front
        ? 'home'
        : headerBlock?.attrs?.header_type != 'header one'
          ? 'fullscreen'
          : 'normal'

      let yoastVariableToTitle = (yoast_title: string) => {
        if (yoast_title == undefined) return ""

        let title = yoast_title.substring(yoast_title.lastIndexOf("%"))
        return title;
      }
      
      let headerToLoad: 'homeHeader' | 'defaultHeader'  = headerKind == 'home' 
        ? 'homeHeader' 
        : 'defaultHeader'

      let images =  undefined

      if(headerToLoad == 'homeHeader') {
        images = headerBlock.attrs.images 
          ? parseWordpressImageUrls(headerBlock.attrs.images[0], headerToLoad).image 
          : undefined
      } else if (headerToLoad == 'defaultHeader') {
        images = parseWordpressImageUrls(headerBlock.attrs.image, headerToLoad).image
      }

      document.title = htmlDecode(res.title.rendered + " - " + "Site")

      let video: string | undefined = (headerBlock?.attrs?.video_url != undefined && headerBlock?.attrs?.video_url != '') 
        ? headerBlock?.attrs?.video_url 
        : undefined

      return {
        kind: 'result',
        status: 200,
        value: {
          blocks: blocks.filter((b: any) => b.blockName != "vidda/header"),
          pageData: {
            header: {
              kind: headerKind,
              pageTitle: htmlDecode(headerBlock?.attrs?.title || ''),
              pageIntro: res?.is_front ? htmlDecode(headerBlock?.attrs?.intro || '') : htmlDecode(headerBlock?.attrs?.subtitle || ''),
              linkPrimary: linkPrimary,
              linkSecondary: linkSecondary,
              videoUrl: video,
              image: images
            },

            page: {
              header: headerKind,
              kind: pageKind,
              node: res as any,
              language: lang
            },
            metadata: {},
            breadcrumbs,
          }
        } as CMSLoaderState
      }
    }
    catch (e) {

      console.error("Error when fetching page", slug)
      console.error(JSON.stringify(e))

      return {
        kind: 'failed',
        status: 500
      }
    }
  }

  export const loadPage: (slug: string ) => AsyncState<CMSLoaderState> = (slug) => {
      return loadingAsyncState(() => getPage(getLangFromUrl(), slug))
    }
}
